import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Volume2, Volume1, VolumeX } from "lucide-react";
import { ASSETS } from "@/lib/assets";

// Words are timed to the actual scene content of hero-banner.mp4 (~43.75s),
// verified frame-by-frame (0.5s sampling) to dodge every baked-in caption
// the footage already carries on its own ("Quick Check-In" app survey at
// ~24-26.5s and ~35-36s, and the "Biometric Sensor Inputs / Heart Rate /
// Blood Oxygen / Skin Temperature" callouts at ~27-30.3s): surf (0-5.5s) →
// biometric readout during swim/walk (6-11.2s) → tennis + app screen
// (12-17.2s) → hiking/cycling/volleyball checking the app (18-20.6s) →
// first clean dispense shot (21-22.4s) → [captioned app-survey + sensor
// callouts run on their own, no overlay text competes with them] → clean
// product/wrist/dispense shots (30.5-34.8s) → beach smile (36.5-38s) →
// animated shield/wordmark reveal, left text-free (38-43.75s).
// BEATS[0] ("intro") plays once at the very start of the loop (0-5.6s),
// before Sense begins at 6.0s — same short window the original opening
// line used. It is deliberately just eyebrow + tagline + H1 + CTAs — the
// fuller "Sunscreen only works..." problem narrative already has its own
// section immediately below the hero ("The Closed Loop System" in
// Home.js), so it is not repeated on top of the video. The gaps between
// word beats (e.g. 11.2-12.0, 22.4-30.5) intentionally show no text, same
// as before — the intro does not re-trigger there.
const BEATS = [
  {
    start: 0,
    end: 5.6,
    kind: "intro",
    eyebrow: "Patented Wearable Dispensing Platform",
    tagline: "Beyond Skin Intelligence",
    heading: (
      <>
        The Wearable That Enables{" "}
        <span className="font-bold" style={{ color: "#ff2d78", textShadow: "0 2px 26px rgba(255,45,120,0.45)" }}>
          Immediate Action
        </span>
      </>
    ),
    paragraph:
      "WearSpray's patented, refillable wrist dispenser puts your chosen compatible skincare formulation one press away. We are developing WearSpray IQ to connect personalized guidance and timely reminders with immediate access to the product itself.",
    ctas: [
      { label: "Explore the Platform", to: "/platform" },
      { label: "Discuss a Partnership", to: "/contact" },
    ],
  },
  { start: 6.0, end: 11.2, kind: "word", big: "Sense", small: "what is happening.", color: "#00e5ff" },
  { start: 12.0, end: 17.2, kind: "word", big: "Understand", small: "the moment.", color: "#3ad6a0" },
  { start: 18.0, end: 20.6, kind: "word", big: "Prompt", small: "at the right time.", color: "#e6c068" },
  { start: 21.0, end: 22.4, kind: "word", big: "Act", small: "instantly.", color: "#ff2d78" },
  { start: 30.5, end: 34.8, kind: "word", big: "Learn", small: "over time.", color: "#b794f6" },
  {
    start: 36.5,
    end: 38.0,
    kind: "line",
    text: (
      <>
        Protection,{" "}
        <span className="text-accent">the moment you need it.</span>
      </>
    ),
  },
  {
    // Intentionally never active on screen (start === end never matches a
    // t >= start && t < end window) so the final shield/wordmark reveal
    // plays without competing text — but the line stays in the DOM/static
    // HTML so "AI skin intelligence" remains crawlable for SEO.
    start: 999,
    end: 999,
    kind: "line",
    text: (
      <>
        <span className="text-accent">AI skin intelligence.</span>
        <br />
        Actionable on the wrist.
      </>
    ),
  },
];

export default function HeroFilm() {
  const videoRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [cueReady, setCueReady] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const fadeFrameRef = useRef(null);
  const muteTimeoutRef = useRef(null);
  // Puppeteer/headless crawlers (react-snap's static pre-render, and most bots)
  // set navigator.webdriver = true. Autoplaying a looping video keeps the
  // network from ever going idle, which stalls react-snap's networkidle0
  // wait indefinitely. Real visitors still get the full autoplaying film.
  const isCrawler = typeof navigator !== "undefined" && navigator.webdriver;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (fadeFrameRef.current) cancelAnimationFrame(fadeFrameRef.current);
      if (muteTimeoutRef.current) clearTimeout(muteTimeoutRef.current);
    };
  }, []);

  // Smoothly ramps video.volume toward `target` over `duration`ms instead of
  // an instant jump, so turning the score on/off feels like a proper fade.
  const fadeVolumeTo = (target, duration = 1000) => {
    const video = videoRef.current;
    if (!video) return;
    if (fadeFrameRef.current) cancelAnimationFrame(fadeFrameRef.current);
    const from = video.volume;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      // .volume throws IndexSizeError for anything outside [0,1], and float
      // interpolation can drift a hair past either edge, so clamp explicitly.
      video.volume = Math.min(1, Math.max(0, from + (target - from) * p));
      if (p < 1) fadeFrameRef.current = requestAnimationFrame(step);
    };
    fadeFrameRef.current = requestAnimationFrame(step);
  };

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    if (muteTimeoutRef.current) {
      clearTimeout(muteTimeoutRef.current);
      muteTimeoutRef.current = null;
    }
    if (!soundOn) {
      // Turning sound on is a direct result of this click (a user gesture),
      // so browsers allow it even though the film started muted for
      // autoplay. Unmute silently first, then fade up to the last volume
      // the visitor set (mid-range by default, not straight to full).
      video.volume = 0;
      video.muted = false;
      fadeVolumeTo(volume, 1000);
      setSoundOn(true);
    } else {
      // Fade the score down, then fully mute once it's inaudible so it
      // never pops back in at volume 1 on the next loop — `volume` (the
      // visitor's chosen level) is preserved for the next unmute.
      fadeVolumeTo(0, 1000);
      muteTimeoutRef.current = setTimeout(() => {
        video.muted = true;
        video.volume = volume;
      }, 1000);
      setSoundOn(false);
    }
  };

  // Dragging the slider takes manual control: cancel any in-flight fade and
  // apply the level immediately, no ramping.
  const handleVolumeChange = (e) => {
    const v = Number(e.target.value) / 100;
    const video = videoRef.current;
    setVolume(v);
    if (!video) return;
    if (fadeFrameRef.current) cancelAnimationFrame(fadeFrameRef.current);
    if (muteTimeoutRef.current) {
      clearTimeout(muteTimeoutRef.current);
      muteTimeoutRef.current = null;
    }
    video.muted = v === 0;
    video.volume = v;
    setSoundOn(v > 0);
  };

  const idxRef = useRef(0);
  const cueReadyRef = useRef(false);
  const rafRef = useRef(null);
  const introShownRef = useRef(false);

  // Driven by requestAnimationFrame (fires ~60x/sec, in step with the
  // display) instead of the video's `timeupdate` event, which many browsers
  // only fire a few times a second — that gap was the main source of the
  // text feeling like it lagged behind the actual scene cut.
  useEffect(() => {
    const tick = () => {
      const t = videoRef.current?.currentTime ?? 0;
      if (t > 42.7 && !cueReadyRef.current) {
        cueReadyRef.current = true;
        setCueReady(true);
      }
      let next = -1;
      for (let i = 0; i < BEATS.length; i++) {
        if (t >= BEATS[i].start && t < BEATS[i].end) next = i;
      }
      // Keep the opening line visible from the very first frame (and in the
      // static pre-render, where the video may never advance) instead of
      // leaving the headline blank until playback reaches 0.4s.
      if (next === -1 && t < BEATS[0].start) next = 0;
      // The intro (beat 0) is only ever shown once, on the film's very
      // first pass through its window — once playback moves past it, it
      // never reappears on later loops (otherwise it would flash back in
      // every ~44s alongside the word beats, which reads as it randomly
      // "popping up" between them).
      if (next === 0) {
        if (introShownRef.current) next = -1;
      } else if (t >= BEATS[0].end) {
        introShownRef.current = true;
      }
      if (next !== idxRef.current) {
        idxRef.current = next;
        setIdx(next);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const beat = idx >= 0 ? BEATS[idx] : null;
  const VolumeIcon = !soundOn || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <section className="relative w-full bg-black overflow-hidden min-h-[100svh] xl:min-h-0" data-testid="home-hero-video">
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover xl:static xl:h-auto xl:w-full xl:block"
        src="/video/hero-banner.mp4"
        poster="/video/hero-banner-poster.jpg"
        autoPlay={!isCrawler}
        loop={!isCrawler}
        preload={isCrawler ? "none" : "auto"}
        muted
        playsInline
        data-testid="home-hero-video-player"

      />

      {/* sound control — the film starts muted so autoplay is allowed by every
          browser; a real visitor can turn the score on with one click, which
          counts as a user gesture, so unmuting is never blocked. Clicking
          reveals a slider (starts mid-range, not straight to full volume);
          dragging it takes over from the fade for direct control. The actual
          .muted / .volume are driven imperatively (fadeVolumeTo/toggleSound/
          handleVolumeChange) so nothing fights React re-applying attributes. */}
      <div className="absolute top-24 sm:top-28 right-6 z-20 flex items-center gap-2">
        <AnimatePresence>
          {soundOn && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 96, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="overflow-hidden flex items-center h-11 px-3.5 rounded-full border border-white/25 bg-black/35 backdrop-blur-md"
              style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.35)" }}
            >
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round(volume * 100)}
                onChange={handleVolumeChange}
                aria-label="Hero video volume"
                data-testid="hero-volume-slider"
                className="w-full accent-accent cursor-pointer"
              />
            </motion.div>
          )}
        </AnimatePresence>
        <button
          type="button"
          onClick={toggleSound}
          aria-label={soundOn ? "Turn hero sound off" : "Turn hero sound on"}
          aria-pressed={soundOn}
          data-testid="hero-sound-toggle"
          className="flex items-center justify-center w-11 h-11 rounded-full border border-white/25 bg-black/35 backdrop-blur-md text-white hover:border-accent hover:text-accent transition-colors shrink-0"
          style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.35)" }}
        >
          <VolumeIcon size={18} />
        </button>
      </div>

      {/* legibility gradient */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/70 via-transparent to-transparent" />

      {/* focus vignette — darkens the lower-left behind the copy only while a beat is showing */}
      <motion.div
        className="absolute inset-0 pointer-events-none z-[5]"
        initial={{ opacity: 0 }}
        animate={{ opacity: beat ? 1 : 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          background:
            "radial-gradient(130% 90% at 26% 92%, rgba(0,0,0,0.78), rgba(0,0,0,0.4) 42%, transparent 70%)",
        }}
        aria-hidden="true"
      />

      {/* synced word overlay — every beat is always present in the DOM (crawlable
          in the static pre-render) so search engines/AI tools see the full
          "Sense/Understand/Prompt/Act/Learn" + "AI skin intelligence" copy,
          not just whichever line the video happens to be on. Only the
          active beat is visible/animated for real visitors; the rest sit at
          opacity:0, absolutely stacked, so the visual behaviour is unchanged.
          The wrapper below has a fixed min-height (not driven by whichever
          beat happens to be active) and every beat is bottom-anchored inside
          it — switching beats never resizes/reflows the box, which is what
          caused the jump/jitter when a "line" beat swapped with a taller
          "word" beat. */}
      <div className="absolute inset-0 flex items-end pointer-events-none z-10 pb-[14%] md:pb-[16%]">
        <div className="w-full max-w-7xl mx-auto relative min-h-[220px] sm:min-h-[190px] md:min-h-[170px] lg:min-h-[160px]">
          {BEATS.map((b, i) => {
            const isActive = i === idx;
            const Tag = i === 0 ? "h1" : "p";
            const content =
              b.kind === "intro" ? (
                <div className="max-w-2xl">
                  <span
                    className="inline-flex items-center font-mono text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#e6c068] border border-[#e6c068]/40 bg-black/40 rounded-full px-3 py-1.5"
                    style={{ textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}
                  >
                    {b.eyebrow}
                  </span>
                  <h1
                    className="font-heading font-normal tracking-tighter leading-[1.05] text-3xl sm:text-5xl md:text-6xl mt-3"
                    style={{ textShadow: "0 2px 30px rgba(0,0,0,0.65)" }}
                  >
                    <span className="block text-accent">{b.tagline}</span>
                    <span className="block text-white">{b.heading}</span>
                  </h1>
                  <p
                    className="font-body text-sm sm:text-base md:text-lg text-zinc-200 leading-relaxed mt-4 max-w-xl"
                    style={{ textShadow: "0 2px 16px rgba(0,0,0,0.75)" }}
                    data-testid="hero-support-paragraph"
                  >
                    {b.paragraph}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-6">
                    {b.ctas.map((cta, ci) => (
                      <Link
                        key={cta.to}
                        to={cta.to}
                        data-testid={`hero-cta-${ci === 0 ? "primary" : "secondary"}`}
                        className={
                          ci === 0
                            ? "font-mono text-[11px] sm:text-xs tracking-[0.15em] uppercase bg-white text-black rounded-full px-5 py-2.5 hover:bg-accent transition-colors"
                            : "font-mono text-[11px] sm:text-xs tracking-[0.15em] uppercase border border-white/40 text-white rounded-full px-5 py-2.5 hover:border-accent hover:text-accent transition-colors"
                        }
                      >
                        {cta.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : b.kind === "line" ? (
                <Tag
                  className="font-heading font-normal tracking-tighter leading-[1.04] text-white text-4xl sm:text-6xl lg:text-7xl max-w-4xl"
                  style={{ textShadow: "0 2px 30px rgba(0,0,0,0.6)" }}
                >
                  {b.text}
                </Tag>
              ) : (
                <div>
                  <span
                    className="block font-heading font-medium tracking-tighter leading-[0.98] text-6xl sm:text-7xl lg:text-8xl"
                    style={{ color: b.color, textShadow: "0 2px 30px rgba(0,0,0,0.6)" }}
                  >
                    {b.big}
                  </span>
                  <span
                    className="block font-heading font-light tracking-tight leading-[1.1] text-white text-2xl sm:text-3xl lg:text-4xl mt-1"
                    style={{ textShadow: "0 2px 24px rgba(0,0,0,0.6)" }}
                  >
                    {b.small}
                  </span>
                </div>
              );
            // Every beat is absolutely stacked at the same spot and bottom-
            // anchored via flex/justify-end, so geometry is 100% static
            // across beat switches — only opacity/blur/y crossfade.
            return (
              <motion.div
                key={i}
                className="absolute inset-y-0 inset-x-6 md:inset-x-12 flex flex-col justify-end"
                initial={false}
                animate={{
                  opacity: isActive ? 1 : 0,
                  y: isActive ? 0 : 10,
                  filter: isActive ? "blur(0px)" : "blur(6px)",
                }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                style={{ pointerEvents: isActive ? "auto" : "none" }}
                aria-hidden={!isActive}
                data-testid={`hero-beat-${i}`}
                data-active={isActive}
              >
                {content}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* early product reveal — a clear look at the actual dispensing product
          and its use, shown alongside the opening beat so visitors see the
          real hardware right away rather than waiting for the film's later
          dispense shots. */}
      <AnimatePresence>
        {(idx === 0 || idx === 4) && (
          <motion.div
            className="absolute top-40 right-4 w-28 sm:w-36 sm:right-6 md:w-44 lg:top-1/2 lg:-translate-y-1/2 lg:right-10 lg:w-64 z-20"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-product-reveal"
          >
            <div
              className="relative rounded-2xl border-2 border-accent/60 bg-black/50 backdrop-blur-md p-3 md:p-4"
              style={{ boxShadow: "0 0 0 1px rgba(0,229,255,0.15), 0 12px 50px rgba(0,0,0,0.55), 0 0 40px rgba(0,229,255,0.18)" }}
            >
              <motion.span
                className="absolute -inset-px rounded-2xl pointer-events-none"
                style={{ boxShadow: "0 0 0 1px rgba(0,229,255,0.5)" }}
                animate={{ opacity: [0.4, 0.9, 0.4] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                aria-hidden="true"
              />
              <img
                src={ASSETS.actionDispense}
                alt="Real WearSpray device dispensing skincare formulation from the wrist into an open palm"
                className="w-full h-auto rounded-lg object-cover"
              />
              <p className="font-mono text-[8px] sm:text-[9px] md:text-[11px] tracking-[0.14em] uppercase text-accent mt-2.5 text-center leading-snug">
                Real product. Real dispense. One press away.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* scroll cue — pops in toward the end of the film so the eye lands on it */}
      <AnimatePresence>
        {cueReady && !scrolled && (
          <motion.div
            className="absolute bottom-16 md:bottom-20 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-none"
            initial={{ opacity: 0, y: 24, scale: 0.7 }}
            animate={{ opacity: 1, y: [0, -6, 0], scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.7 }}
            transition={{
              opacity: { duration: 0.5 },
              scale: { type: "spring", stiffness: 320, damping: 16 },
              y: { duration: 2.2, repeat: Infinity, ease: "easeInOut" },
            }}
            data-testid="hero-scroll-cue"
          >
            <span className="font-mono text-[11px] sm:text-sm tracking-[0.28em] uppercase text-white" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.85)" }}>
              Explore further
            </span>
            <motion.span
              animate={{ y: [0, 7, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChevronDown size={24} className="text-accent" style={{ filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.85))" }} />
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
